import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ArrowLeft, Users, Loader2, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  getCircle, listCirclePosts, joinCircle, isCircleMember, createCirclePost,
} from "@/lib/community.functions";
import { absoluteUrl } from "@/lib/site";
import { ShareButton } from "@/components/community/ShareButton";

export const Route = createFileRoute("/circles/$slug")({
  loader: async ({ params }) => {
    // We only need the slug for the head(); the component fetches full data.
    return { slug: params.slug };
  },
  head: ({ loaderData }) => {
    const slug = loaderData?.slug ?? "";
    const url = absoluteUrl(`/circles/${slug}`);
    const title = `${slug.replace(/-/g, " ")} — Discussion Circle · KAfrequency`;
    return {
      meta: [
        { title },
        { name: "description", content: "A KAfrequency discussion circle. Reflections and sacred talk with fellow practitioners." },
        { property: "og:title", content: title },
        { property: "og:description", content: "Reflections and sacred talk with fellow practitioners." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Buddha KA · KAfrequency" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: CirclePage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-serif text-3xl">Circle not found</h1>
      <Link to="/circles" className="mt-6 inline-block text-gold hover:text-gold-luminous">← Return to circles</Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-serif text-3xl">Something went wrong</h1>
      <Link to="/circles" className="mt-6 inline-block text-gold hover:text-gold-luminous">← Return to circles</Link>
    </div>
  ),
});

function formatTime(iso: string) {
  const d = new Date(iso); const diff = Date.now() - d.getTime(); const m = Math.floor(diff / 60000);
  if (m < 1) return "just now"; if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24); if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function CirclePage() {
  const { slug } = Route.useLoaderData();
  const qc = useQueryClient();
  const [signedIn, setSignedIn] = useState(false);
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSignedIn(Boolean(data.session)));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSignedIn(Boolean(s)));
    return () => sub.subscription.unsubscribe();
  }, []);

  const getCircleFn = useServerFn(getCircle);
  const listPostsFn = useServerFn(listCirclePosts);
  const joinFn = useServerFn(joinCircle);
  const isMemberFn = useServerFn(isCircleMember);
  const createPostFn = useServerFn(createCirclePost);

  const circle = useQuery({
    queryKey: ["circle", slug],
    queryFn: () => getCircleFn({ data: { slug } }),
  });

  useEffect(() => {
    if (circle.data === null) throw notFound();
  }, [circle.data]);

  const circleId = circle.data?.id;

  const posts = useQuery({
    queryKey: ["circle-posts", circleId],
    enabled: Boolean(circleId),
    queryFn: () => listPostsFn({ data: { circleId: circleId! } }),
    staleTime: 15_000,
  });

  const member = useQuery({
    queryKey: ["circle-member", circleId, signedIn],
    enabled: Boolean(circleId) && signedIn,
    queryFn: () => isMemberFn({ data: { circleId: circleId! } }),
    staleTime: 30_000,
  });

  const joinMut = useMutation({
    mutationFn: () => joinFn({ data: { circleId: circleId! } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["circle-member", circleId] }),
  });

  const postMut = useMutation({
    mutationFn: () => createPostFn({
      data: { circleId: circleId!, body: body.trim(), title: title.trim() || undefined },
    }),
    onSuccess: () => {
      setBody(""); setTitle("");
      qc.invalidateQueries({ queryKey: ["circle-posts", circleId] });
    },
  });

  if (circle.isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <Loader2 className="mx-auto animate-spin text-gold" />
      </div>
    );
  }
  if (!circle.data) return null;

  const isMember = member.data === true;

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
      <Link to="/circles" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-foreground hover:text-gold">
        <ArrowLeft size={12} /> All circles
      </Link>

      <header className="mt-8">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
          <Users size={12} /> Circle
        </div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">{circle.data.title}</h1>
        {circle.data.description && (
          <p className="mt-4 max-w-2xl text-muted-foreground">{circle.data.description}</p>
        )}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {signedIn ? (
            isMember ? (
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-xs uppercase tracking-widest text-emerald-300">
                Member
              </span>
            ) : (
              <button
                onClick={() => joinMut.mutate()}
                disabled={joinMut.isPending}
                className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2 text-xs font-semibold uppercase tracking-widest text-obsidian shadow-gold disabled:opacity-60 min-h-[36px]"
              >
                Join circle
              </button>
            )
          ) : (
            <a href="/auth" className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2 text-xs font-semibold uppercase tracking-widest text-obsidian shadow-gold min-h-[36px]">
              Sign in to join
            </a>
          )}
          <ShareButton title={`${circle.data.title} — KAfrequency Circle`} url={`/circles/${slug}`} />
        </div>
      </header>

      {isMember && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!body.trim() || postMut.isPending) return;
            postMut.mutate();
          }}
          className="mt-10 rounded-2xl border hairline bg-obsidian/40 p-5 focus-within:border-gold"
        >
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Title (optional)"
            className="w-full bg-transparent font-serif text-lg text-ivory placeholder:text-muted-foreground focus:outline-none"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            maxLength={8000}
            placeholder="Share with the circle…"
            className="mt-2 w-full resize-none bg-transparent text-sm text-ivory placeholder:text-muted-foreground focus:outline-none"
          />
          <div className="mt-3 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{body.length}/8000</span>
            <button
              type="submit"
              disabled={!body.trim() || postMut.isPending}
              className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2 text-xs font-semibold uppercase tracking-widest text-obsidian shadow-gold disabled:opacity-50 min-h-[36px]"
            >
              {postMut.isPending && <Loader2 size={12} className="animate-spin" />}
              Post
            </button>
          </div>
        </form>
      )}

      <section className="mt-12">
        <div className="mb-6 flex items-center gap-2">
          <MessageCircle size={16} className="text-gold" />
          <h2 className="text-xs uppercase tracking-[0.3em] text-gold">Reflections</h2>
          <span className="text-xs text-muted-foreground">· {posts.data?.length ?? 0}</span>
        </div>

        {posts.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (posts.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">The circle is quiet. Be the first voice.</p>
        ) : (
          <ul className="space-y-6">
            {posts.data?.map((p) => (
              <li key={p.id} className="rounded-2xl border hairline bg-obsidian/40 p-5">
                {p.title && <h3 className="font-serif text-xl text-ivory">{p.title}</h3>}
                <p className={`whitespace-pre-wrap text-sm text-ivory/85 ${p.title ? "mt-2" : ""}`}>{p.body}</p>
                <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                  <span>{p.author_display_name ?? "Practitioner"}</span>
                  <span>{formatTime(p.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
